import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { getApiUrl } from '../../../../utils/ApiUtils';
import axiosInstance from '../../../../services/AxiosInstance';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';
import { toast } from 'sonner';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    Calculator,
    BookCheck,
    Lock,
    RefreshCw,
    HelpCircle,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';

/** Hover/focus-tooltip bakom en liten ?-ikon — svaret på "vad betyder det
 *  här?" utan att texta ned hela steget. */
const HelpTip = ({ text }) => (
    <span className="ye-help" tabIndex={0} aria-label={text}>
        <HelpCircle size={14} />
        <span className="ye-help-tip" role="tooltip">{text}</span>
    </span>
);

/** Labels for the booked verifikat chain — the keys the backend's
 *  `verifications` object may contain. */
const VERIFICATION_LABELS = {
    aterforing: 'Återföring av periodiseringsfond',
    avsattning: 'Avsättning till periodiseringsfond',
    skatt: 'Årets skatt',
    resultat: 'Årets resultat',
};

/** Parse a Swedish-typed amount ("12 500,50") into a number. */
function parseAmount(raw) {
    if (raw == null) return NaN;
    const cleaned = String(raw).replace(/[\s ]/g, '').replace(',', '.');
    if (cleaned === '') return NaN;
    return Number(cleaned);
}

/** Empty input = 0 (both amount fields default to zero). */
function parseOptionalAmount(raw) {
    if (String(raw ?? '').trim() === '') return 0;
    return parseAmount(raw);
}

/**
 * Step 4 — Skatt & dispositioner: compute and book the year's tax chain.
 * A single card, top to bottom: read-only Underlag (from a silent
 * tax/compute on mount), the periodiseringsfond decisions (reversals +
 * avsättning), underskott from last year, "Beräkna" (tax/compute with
 * the chosen inputs — nothing booked), then "Bokför" (tax/book). Any
 * input change invalidates the shown result, like AdjustmentsStep
 * proposals. If the backend says the chain is already booked
 * (data.booked non-null) the card starts in the booked-state summary
 * with an inline-confirm undo (DELETE tax). Completing the step LOCKS
 * the fiscal year server-side (409 with a Swedish message if the tax
 * isn't booked); undoing the step reopens the year — the page refetches
 * the overview after both. Markup is the modern-card composition;
 * shared ye-* chrome from YearEndPage.
 */
const TaxStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [data, setData] = useState(null); // latest tax/compute payload
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Inputs (section 2–3). Empty string = 0.
    const [pfondInput, setPfondInput] = useState('');
    const [underskottInput, setUnderskottInput] = useState('');
    const [selectedYears, setSelectedYears] = useState([]);
    const selectionInitialized = useRef(false);

    // Fonder vars avsättningsår inte går att läsa ur kontonamnet — backend
    // vägrar räkna (422 med details.unresolvedFunds) tills året är angivet,
    // eftersom 6-årsåterföringen (IL 30:7) annars inte kan bevakas.
    const [unresolvedFunds, setUnresolvedFunds] = useState([]);
    // Klassificeringsgrind (2026-08-11; PER KONTO sedan 2026-08-12):
    // nedskrivningar av finansiella tillgångar kräver kapitaltillgång/lager-
    // besked per konto innan beräkning — ett bolag kan hålla båda sorterna.
    const [nedskrivningKrav, setNedskrivningKrav] = useState(null);
    const [nedskrivningKlassPerKonto, setNedskrivningKlassPerKonto] = useState({});
    const nedskrivningKlassRef = useRef({});
    nedskrivningKlassRef.current = nedskrivningKlassPerKonto;
    const [fundYearInputs, setFundYearInputs] = useState({});
    // Ref-spegel så fetchInitial kan läsa senaste åren utan att själva
    // callbacken byter identitet för varje tangenttryck (→ refetch-loop).
    const fundYearInputsRef = useRef({});
    useEffect(() => { fundYearInputsRef.current = fundYearInputs; }, [fundYearInputs]);

    // Result of an explicit "Beräkna" — null until computed, and
    // invalidated by any input change (the "fresh result" gate for
    // "Bokför").
    const [result, setResult] = useState(null);
    const [computing, setComputing] = useState(false);
    const [booking, setBooking] = useState(false);
    const [actionError, setActionError] = useState(null);

    const [verifications, setVerifications] = useState(null);
    const [confirmUndo, setConfirmUndo] = useState(false);
    const [undoing, setUndoing] = useState(false);

    // Verifikat-modal (samma VerificationDetailsModal som verifikatlistan).
    const [modalVerification, setModalVerification] = useState(null);
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    const openVerification = async (verificationId) => {
        try {
            const { data: v } = await axiosInstance.get(
                getApiUrl(`${companyId}/verification/${verificationId}`),
                { withCredentials: true },
            );
            const debitSum = (v.transactions || [])
                .filter((t) => t.transaction_type === 'debit')
                .reduce((s, t) => s + Number(t.amount || 0), 0);
            setModalVerification({ ...v, debitSum, creditSum: debitSum });
            setShowVerificationModal(true);
        } catch (err) {
            toast.error('Kunde inte hämta verifikatet.');
        }
    };

    // ---- Initial load (silent compute with defaults) ----------------------

    const buildFundAssignments = useCallback(() => {
        const assignments = {};
        Object.entries(fundYearInputsRef.current).forEach(([konto, year]) => {
            const y = parseInt(year, 10);
            if (Number.isInteger(y)) assignments[konto] = y;
        });
        const out = Object.keys(assignments).length ? { fundYearAssignments: assignments } : {};
        const klasser = Object.fromEntries(
            Object.entries(nedskrivningKlassRef.current)
                .filter(([, v]) => v === 'kapitaltillgang' || v === 'lager'),
        );
        if (Object.keys(klasser).length) out.nedskrivningFinKlassificeringPerKonto = klasser;
        return out;
    }, []);

    const fetchInitial = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.computeTax(companyId, processId, buildFundAssignments());
            const payload = resp?.data || null;
            setData(payload);
            setUnresolvedFunds([]);
            if (!selectionInitialized.current) {
                selectionInitialized.current = true;
                setSelectedYears(payload?.funds?.forcedReversalYears || []);
            }
            setVerifications(payload?.booked?.verifications
                || payload?.verifications
                || null);
        } catch (err) {
            const details = err.response?.data?.details;
            if (details?.unresolvedFunds?.length) {
                setUnresolvedFunds(details.unresolvedFunds);
                // The label's year is EVIDENCE the user confirms or corrects
                // (review 2026-09-05, F4) — prefilled, never applied on its own.
                setFundYearInputs((prev) => {
                    const next = { ...prev };
                    for (const f of details.unresolvedFunds) {
                        if (!next[f.accountNumber] && f.proposedYear) next[f.accountNumber] = String(f.proposedYear);
                    }
                    return next;
                });
                setLoadError(null);
            } else if (details?.requires === 'nedskrivningFinKlassificeringPerKonto'
                || details?.requires === 'nedskrivningFinKlassificering') {
                setNedskrivningKrav(details);
                setLoadError(null);
            } else {
                setLoadError(err.response?.data?.message
                    || 'Kunde inte hämta skatteunderlaget. Försök igen senare.');
            }
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId, buildFundAssignments]);

    useEffect(() => { fetchInitial({ silent: true }); }, [fetchInitial]);

    const computation = data?.computation || null;
    const funds = data?.funds || null;
    const forcedYears = funds?.forcedReversalYears || [];
    const booked = data?.booked?.booked_at ? data.booked : null;

    // ---- Inputs -----------------------------------------------------------

    /** Any edit invalidates the computed result — force a new "Beräkna". */
    const invalidate = () => {
        setResult(null);
        setActionError(null);
    };

    const toggleYear = (year) => {
        if (forcedYears.includes(year)) return; // forced — cannot be unchecked
        setSelectedYears((prev) => (prev.includes(year)
            ? prev.filter((y) => y !== year)
            : [...prev, year]));
        invalidate();
    };

    const pfondValue = parseOptionalAmount(pfondInput);
    const underskottValue = parseOptionalAmount(underskottInput);
    const inputsValid = Number.isFinite(pfondValue) && pfondValue >= 0
        && Number.isFinite(underskottValue) && underskottValue >= 0;

    const buildBody = () => ({
        pfondAvsattning: pfondValue,
        aterforingFundYears: selectedYears,
        underskottPrevYear: underskottValue,
        ...buildFundAssignments(),
    });

    // ---- Actions ----------------------------------------------------------

    const handleCompute = async () => {
        setComputing(true);
        setActionError(null);
        try {
            const resp = await YearEndService.computeTax(companyId, processId, buildBody());
            const payload = resp?.data || null;
            setData(payload);
            setResult(payload?.computation || null);
        } catch (err) {
            setActionError(err.response?.data?.message
                || 'Kunde inte beräkna skatten. Försök igen.');
        } finally {
            setComputing(false);
        }
    };

    const handleBook = async () => {
        setBooking(true);
        setActionError(null);
        try {
            const resp = await YearEndService.bookTax(companyId, processId, buildBody());
            toast.success('Skatten och årets resultat är bokförda som verifikat.');
            // Backend returns { booked: true, computation, verifications } —
            // `booked` is a BOOLEAN there, while renderBody switches on
            // data.booked.booked_at. Set an optimistic object immediately
            // (no flash back to the form), then refetch the real state.
            setData((prev) => (prev ? {
                ...prev,
                booked: { booked_at: new Date().toISOString() },
                computation: resp?.data?.computation || prev.computation,
            } : prev));
            setVerifications(resp?.data?.verifications || null);
            setResult(null);
            await fetchInitial({ silent: true });
        } catch (err) {
            setActionError(err.response?.data?.message
                || 'Kunde inte bokföra skatten och årets resultat.');
        } finally {
            setBooking(false);
        }
    };

    const handleUndoBooking = async () => {
        setUndoing(true);
        try {
            await YearEndService.undoTax(companyId, processId);
            toast.success('Bokföringen är ångrad — rättelseverifikat har skapats.');
            setConfirmUndo(false);
            setVerifications(null);
            setResult(null);
            await fetchInitial({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ångra bokföringen.');
        } finally {
            setUndoing(false);
        }
    };

    // ---- Renderers --------------------------------------------------------

    const renderUnderlag = () => {
        if (!computation) return null;
        const j = computation.justeringar || {};
        const basis = data?.schablonintaktBasis || {};
        const ejAvdragsgilla = j.ejAvdragsgilla || [];
        const ejSkattepliktiga = j.ejSkattepliktiga || [];
        return (
            <div className="ye-tax-section">
                <h5 className="ye-section-heading">
                    1. Underlag — vad skatten räknas på
                    <HelpTip text="Skatten beräknas inte på det bokförda resultatet rakt av. Vissa kostnader får inte dras av och vissa intäkter är skattefria — här ser du hur det bokförda resultatet justeras till det skattemässiga. Allt hämtas från bokföringen, du behöver inte fylla i något." />
                </h5>
                <div className="ye-tax-rows">
                    <div className="ye-tax-row">
                        <span>
                            Resultat före bokslutsdispositioner
                            <HelpTip text="Årets intäkter minus kostnader innan skatt och dispositioner som periodiseringsfond — startpunkten för skatteberäkningen." />
                        </span>
                        <span className="ye-num">{formatSek(computation.inputs?.resultatForeDispositioner)}</span>
                    </div>

                    <div className="ye-tax-row ye-tax-row-group">
                        <span>
                            Ej avdragsgilla kostnader
                            <HelpTip text="Kostnader som sänker det bokförda resultatet men som skattereglerna inte godkänner som avdrag — till exempel representation över gränsen, medlemsavgifter och förseningsavgifter. De läggs tillbaka och höjer den skattepliktiga inkomsten." />
                        </span>
                        <span className="ye-num">{formatSek(j.sumEjAvdragsgilla)}</span>
                    </div>
                    {ejAvdragsgilla.map((row) => (
                        <div className="ye-tax-row ye-tax-row-sub" key={`ea-${row.account}`}>
                            <span>{row.account} — {row.name}</span>
                            <span className="ye-num">{formatSek(row.amount)}</span>
                        </div>
                    ))}

                    <div className="ye-tax-row ye-tax-row-group">
                        <span>
                            Ej skattepliktiga intäkter
                            <HelpTip text="Intäkter som finns i bokföringen men inte beskattas — till exempel vissa utdelningar och ränta på skattekontot. De dras bort och sänker den skattepliktiga inkomsten." />
                        </span>
                        <span className="ye-num">{formatSek(j.sumEjSkattepliktiga)}</span>
                    </div>
                    {ejSkattepliktiga.map((row) => (
                        <div className="ye-tax-row ye-tax-row-sub" key={`es-${row.account}`}>
                            <span>{row.account} — {row.name}</span>
                            <span className="ye-num">{formatSek(row.amount)}</span>
                        </div>
                    ))}

                    <div className="ye-tax-row ye-tax-row-group">
                        <span>
                            Schablonintäkt på periodiseringsfonder
                            <HelpTip text="Staten tar en årlig 'ränta' för uppskovet: en schablonintäkt beräknad på fondernas värde vid årets ingång läggs till den skattepliktiga inkomsten. Den bokförs aldrig — den finns bara i skatteberäkningen." />
                        </span>
                        <span className="ye-num">{formatSek(j.schablonintaktPfond)}</span>
                    </div>
                </div>
                <div className="ye-well">
                    Schablonintäkten är en skattepliktig schablonränta på periodiseringsfonderna
                    vid årets ingång — den påverkar bara skatten, inte bokföringen.
                    {basis.openingFundTotal != null && (
                        <> Underlag: {formatSek(basis.openingFundTotal)} × {basis.slrPct} %.</>
                    )}
                </div>
            </div>
        );
    };

    const renderFunds = () => {
        const opening = funds?.opening || [];
        return (
            <div className="ye-tax-section">
                <h5 className="ye-section-heading">
                    2. Periodiseringsfonder
                    <HelpTip text="En periodiseringsfond låter dig skjuta upp skatten på upp till 25 % av årets vinst. Skatten försvinner inte — fonden måste tas upp till beskattning igen (återföras) senast sjätte året efter avsättningen, och under tiden betalar du en årlig schablonränta." />
                </h5>

                {opening.length > 0 && (
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '0.75rem', maxWidth: '68ch' }}>
                        <strong>När återför man?</strong> Du väljer själv vilket år inom sex — bocka
                        i en fond så räknas den in i årets beskattning. Smartast är ofta att
                        återföra ett år med förlust, så vinsten kvittas mot förlusten utan skatt.
                        Fonder som nått sista året bockas i automatiskt och kan inte väljas bort.
                    </p>
                )}

                {opening.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                        Företaget har inga periodiseringsfonder sedan tidigare år — det finns
                        inget att återföra.
                    </p>
                ) : (
                    <div className="ye-fund-list">
                        {opening.map((fund) => {
                            const forced = forcedYears.includes(fund.year);
                            const checked = forced || selectedYears.includes(fund.year);
                            const deadline = Number.isInteger(fund.year) ? fund.year + 6 : null;
                            return (
                                <button
                                    type="button"
                                    key={fund.year}
                                    className={`ye-fund-row${checked ? ' selected' : ''}${forced ? ' forced' : ''}`}
                                    onClick={() => toggleYear(fund.year)}
                                    aria-pressed={checked}
                                    disabled={forced}
                                >
                                    <span className={`ye-fund-check${checked ? ' on' : ''}`} aria-hidden="true">
                                        {checked && <CheckCircle2 size={15} />}
                                    </span>
                                    <span className="ye-fund-main">
                                        <span className="ye-fund-name">
                                            <strong>{fund.accountNumber}</strong> — {fund.accountName || `Periodiseringsfond ${fund.year}`}
                                        </span>
                                        {deadline != null && (
                                            <span className="ye-fund-deadline">
                                                Avsatt {fund.year} · återförs senast {deadline}
                                            </span>
                                        )}
                                    </span>
                                    {forced && (
                                        <span className="ye-badge ye-badge-warning">
                                            Sista året — återförs nu
                                        </span>
                                    )}
                                    <span className="ye-fund-balance">{formatSek(fund.balance)}</span>
                                </button>
                            );
                        })}
                        <div className="ye-fund-hint">
                            Klicka på en fond för att återföra den i år.
                        </div>
                    </div>
                )}

                <div className="ye-form-grid">
                    <div className="ye-field">
                        <label htmlFor="ye-tax-pfond">
                            Ny avsättning i år (kr)
                            <HelpTip text="Vill du skjuta upp skatt på en del av årets vinst? Ange beloppet här, så bokförs avsättningen (8811/21xx) tillsammans med skatten. Lämna 0 om du inte vill sätta av något — det är helt frivilligt." />
                        </label>
                        <input
                            id="ye-tax-pfond"
                            type="text"
                            inputMode="decimal"
                            placeholder="0"
                            value={pfondInput}
                            onChange={(e) => { setPfondInput(e.target.value); invalidate(); }}
                        />
                        <div className="ye-field-help">
                            {computation?.maxAvsattning != null
                                ? `Upp till ${formatSek(computation.maxAvsattning)} — 25 % av årets skattemässiga överskott.`
                                : 'Högst 25 % av årets skattemässiga överskott.'}
                        </div>
                    </div>
                </div>
                <div className="ye-well">
                    En avsättning skjuter upp skatten, den tar inte bort den — och varje år
                    kostar uppskovet en schablonränta (syns under Underlag ovan). Den är mest
                    värd när du väntar dig ett svagare år att återföra mot, eller vill jämna
                    ut resultatet mellan år.
                </div>
            </div>
        );
    };

    const renderUnderskott = () => (
        <div className="ye-tax-section">
            <h5 className="ye-section-heading">
                3. Underskott från tidigare år
                <HelpTip text="Gick företaget med förlust förra året? Skattemässiga underskott rullas framåt och dras av mot årets vinst, så att du inte betalar skatt förrän förlusten är återhämtad. Beloppet står i förra årets deklaration (INK2)." />
            </h5>
            <div className="ye-form-grid">
                <div className="ye-field">
                    <label htmlFor="ye-tax-underskott">Underskott (kr)</label>
                    <input
                        id="ye-tax-underskott"
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        value={underskottInput}
                        onChange={(e) => { setUnderskottInput(e.target.value); invalidate(); }}
                    />
                    <div className="ye-field-help">
                        Beloppet finns på förra årets INK2. Har du inte haft underskott
                        lämnar du 0.
                    </div>
                </div>
            </div>
        </div>
    );

    const renderResult = () => {
        if (!result) return null;
        const isLoss = Number(result.aretsResultat) < 0;
        const underskottUt = Number(result.underskottUt) || 0;
        return (
            <div className="ye-proposal">
                <div className="ye-proposal-head">
                    <span className="ye-badge ye-badge-primary">Beräknat — inget är bokfört ännu</span>
                </div>

                <div className="ye-tax-rows" style={{ marginTop: '0.75rem' }}>
                    <div className="ye-tax-row">
                        <span>Beskattningsbar inkomst</span>
                        <span className="ye-num">{formatSek(result.beskattningsbarInkomst)}</span>
                    </div>
                    {result.formulas?.beskattningsbar && (
                        <div className="ye-tax-formula">{result.formulas.beskattningsbar}</div>
                    )}
                </div>

                <div className="ye-tax-figures">
                    <div className="ye-tax-figure">
                        <span className="ye-tax-figure-label">
                            Skatt{result.bolagsskattPct != null ? ` (bolagsskatt ${result.bolagsskattPct} %)` : ''}
                        </span>
                        <span className="ye-tax-figure-value">{formatSek(result.skatt)}</span>
                        {result.formulas?.skatt && (
                            <span className="ye-tax-formula">{result.formulas.skatt}</span>
                        )}
                    </div>
                    <div className="ye-tax-figure">
                        <span className="ye-tax-figure-label">
                            Årets resultat
                            {isLoss && (
                                <span className="ye-badge ye-badge-danger" style={{ marginLeft: 8 }}>
                                    Förlust
                                </span>
                            )}
                        </span>
                        <span className="ye-tax-figure-value">{formatSek(result.aretsResultat)}</span>
                        {result.formulas?.aretsResultat && (
                            <span className="ye-tax-formula">{result.formulas.aretsResultat}</span>
                        )}
                    </div>
                </div>

                {underskottUt > 0 && (
                    <div className="ye-well">
                        {formatSek(underskottUt)} i underskott rullas till nästa år och kan
                        kvittas mot framtida vinster.
                    </div>
                )}

                <div className="ye-proposal-actions">
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleBook}
                        disabled={booking}
                    >
                        <BookCheck size={16} />
                        {booking ? 'Bokför…' : 'Bokför skatt och årets resultat'}
                    </button>
                    <span className="ye-footer-hint">
                        Återföring, avsättning, skatt och årets resultat bokförs som verifikat.
                    </span>
                </div>
            </div>
        );
    };

    const renderComputeSection = () => (
        <div className="ye-tax-section">
            <h5 className="ye-section-heading">
                4. Beräkna och bokför
                <HelpTip text="Beräkna visar hela skatteuträkningen utifrån dina val ovan — inget bokförs. När du är nöjd bokför du: återföringar, avsättning, skatt och årets resultat blir varsitt verifikat, och allt går att ångra med rättelseverifikat. Årets resultat bokförs sist av allt (till konto 2099) — det är årets sista verifikat, och därför låses året direkt efteråt. Vad resultatet sedan används till bestämmer stämman, och det bokförs i det nya året i sista steget." />
            </h5>
            {actionError && (
                <div className="alert alert-danger mb-3" style={{ fontSize: '0.875rem' }}>
                    {actionError}
                </div>
            )}
            {result ? renderResult() : (
                <div className="ye-proposal-actions" style={{ marginTop: 0 }}>
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleCompute}
                        disabled={computing || !inputsValid}
                    >
                        <Calculator size={16} />
                        {computing ? 'Beräknar…' : 'Beräkna'}
                    </button>
                    <span className="ye-footer-hint">
                        Inget bokförs ännu — du får först se beräkningen.
                    </span>
                </div>
            )}
        </div>
    );

    const renderBookedState = () => {
        // Det BOKFÖRDA talet är snapshotet från bokföringstillfället — inte
        // data.computation, som alltid är en färsk beräkning med de inputs
        // som senast skickades (vid sidladdning: bara default-värden). Att
        // visa den färska beräkningen här gav olika siffror före/efter
        // bokföring när användaren valt avsättning/återföring (user find).
        const snapshot = (() => {
            const s = booked?.snapshot;
            if (!s) return null;
            if (typeof s === 'string') { try { return JSON.parse(s); } catch (e) { return null; } }
            return s;
        })();
        const shown = snapshot || computation;
        // Verifikatlistan: efter en nyss gjord bokning finns `verifications`
        // (book-svaret); vid sidladdning härleds den ur bokningsraden.
        const bookedVerifications = verifications || {
            aterforing: booked?.aterforing_verification_id,
            avsattning: booked?.pfond_verification_id,
            skatt: booked?.tax_verification_id,
            resultat: booked?.result_verification_id,
        };
        const bookedKeys = Object.keys(VERIFICATION_LABELS)
            .filter((key) => bookedVerifications[key] != null);
        return (
            <div className="ye-tax-booked">
                <div className="ye-proposal-head">
                    <span className="ye-badge ye-badge-success">
                        <CheckCircle2 size={13} />
                        Bokförd
                    </span>
                    {booked?.booked_at && (
                        <span className="ye-proposal-title">
                            Skatten och årets resultat bokfördes {moment(booked.booked_at).format('YYYY-MM-DD')}.
                        </span>
                    )}
                </div>

                {shown && (
                    <div className="ye-tax-figures">
                        <div className="ye-tax-figure">
                            <span className="ye-tax-figure-label">Skatt</span>
                            <span className="ye-tax-figure-value">{formatSek(shown.skatt)}</span>
                        </div>
                        <div className="ye-tax-figure">
                            <span className="ye-tax-figure-label">
                                Årets resultat
                                {Number(shown.aretsResultat) < 0 && (
                                    <span className="ye-badge ye-badge-danger" style={{ marginLeft: 8 }}>
                                        Förlust
                                    </span>
                                )}
                            </span>
                            <span className="ye-tax-figure-value">{formatSek(shown.aretsResultat)}</span>
                        </div>
                    </div>
                )}

                {bookedKeys.length > 0 && (
                    <div className="ye-well">
                        Bokförda verifikat — klicka för att öppna:
                        <ul>
                            {bookedKeys.map((key) => {
                                const ref = booked?.verificationRefs?.[key];
                                const id = ref?.verificationId || bookedVerifications[key];
                                return (
                                    <li key={key}>
                                        {VERIFICATION_LABELS[key]}
                                        {id && (
                                            <>
                                                {' — '}
                                                <button
                                                    type="button"
                                                    className="ye-verifikat-link"
                                                    onClick={() => openVerification(id)}
                                                >
                                                    {ref?.label || 'Visa verifikat'}
                                                </button>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {completed ? (
                    <div className="ye-field-help" style={{ marginTop: '0.75rem' }}>
                        Året är låst — ångra markeringen längst ner om du behöver ändra
                        bokföringen.
                    </div>
                ) : (
                    <div style={{ marginTop: '1rem' }}>
                        <button
                            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                            onClick={() => setConfirmUndo(true)}
                            disabled={undoing || confirmUndo}
                        >
                            <Undo2 size={14} />
                            Ångra bokföringen
                        </button>
                        {confirmUndo && (
                            <div className="ye-inline-confirm">
                                <span>
                                    Verifikaten nollställs med rättelseverifikat — inget raderas
                                    ur bokföringen. Vill du fortsätta?
                                </span>
                                <span className="ye-inline-confirm-actions">
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={handleUndoBooking}
                                        disabled={undoing}
                                    >
                                        {undoing ? 'Ångrar…' : 'Ja, ångra bokföringen'}
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setConfirmUndo(false)}
                                        disabled={undoing}
                                    >
                                        Avbryt
                                    </button>
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderBody = () => {
        if (unresolvedFunds.length) {
            const allFilled = unresolvedFunds.every((f) => {
                const y = parseInt(fundYearInputs[f.accountNumber], 10);
                return Number.isInteger(y) && y >= 1990 && y < 2100;
            });
            return (
                <>
                    <div className="alert alert-warning" style={{ fontSize: '0.875rem' }}>
                        Avsättningsåret för fonderna nedan är inte bekräftat. Ange vilket
                        beskattningsår varje fond avsattes — utan året kan kravet på återföring
                        senast år sex (IL 30 kap. 7 §) inte bevakas. Året i kontonamnet är bara
                        ett förslag: kontrollera det mot bokslutet det år fonden sattes av.
                        Bekräftade år sparas när skatten bokförs.
                    </div>
                    <div className="ye-form-grid" style={{ marginBottom: '0.75rem' }}>
                        {unresolvedFunds.map((f) => (
                            <div className="ye-field" key={f.accountNumber}>
                                <label htmlFor={`ye-fund-year-${f.accountNumber}`}>
                                    {f.accountNumber} — {f.accountName || 'Periodiseringsfond'}
                                    {' '}({formatSek(f.balance)})
                                    {f.proposedYear ? ` — förslag från kontonamnet: ${f.proposedYear}` : ''}
                                </label>
                                <input
                                    id={`ye-fund-year-${f.accountNumber}`}
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="Avsättningsår, t.ex. 2021"
                                    value={fundYearInputs[f.accountNumber] || ''}
                                    onChange={(e) => setFundYearInputs((prev) => ({
                                        ...prev, [f.accountNumber]: e.target.value,
                                    }))}
                                />
                            </div>
                        ))}
                    </div>
                    <button
                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchInitial()}
                        disabled={refreshing || !allFilled}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Fortsätt med angivna år
                    </button>
                </>
            );
        }
        if (nedskrivningKrav) {
            const rows = nedskrivningKrav.unclassified || [];
            const allClassified = rows.length > 0 && rows.every((r) => {
                const v = nedskrivningKlassPerKonto[r.accountNumber];
                return v === 'kapitaltillgang' || v === 'lager';
            });
            return (
                <>
                    <div className="alert alert-warning" style={{ fontSize: '0.875rem', maxWidth: '72ch' }}>
                        Bokföringen innehåller nedskrivningar/återföringar av finansiella
                        tillgångar. Skattebehandlingen beror på vad tillgångarna är — och
                        bedöms <strong>per konto</strong>, eftersom ett bolag kan hålla båda
                        sorterna samtidigt: för <strong>kapitaltillgångar</strong> är
                        nedskrivningen inte avdragsgill (återläggs på INK2S 4.3 b, återföring
                        undantas på 4.5 c); utgör innehavet <strong>skattemässigt lager</strong>{' '}
                        av finansiella instrument är den avdragsgill (IL 17 kap.) och ingen
                        justering görs. Vid osäkerhet, stäm av med rådgivare.
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                        {rows.map((r) => (
                            <div key={r.accountNumber} className="d-flex align-items-center gap-2" style={{ marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ minWidth: '22ch' }}>
                                    <strong>{r.accountNumber}</strong>
                                    {r.accountName ? ` ${r.accountName}` : ''}
                                    {' '}({r.kind === 'aterforing' ? 'återföring' : 'nedskrivning'}
                                    {' '}{formatSek(Math.abs(r.net || 0))})
                                </span>
                                <button
                                    className={`btn btn-sm ${nedskrivningKlassPerKonto[r.accountNumber] === 'kapitaltillgang' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setNedskrivningKlassPerKonto((prev) => ({ ...prev, [r.accountNumber]: 'kapitaltillgang' }))}
                                >
                                    Kapitaltillgång (återlägg)
                                </button>
                                <button
                                    className={`btn btn-sm ${nedskrivningKlassPerKonto[r.accountNumber] === 'lager' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setNedskrivningKlassPerKonto((prev) => ({ ...prev, [r.accountNumber]: 'lager' }))}
                                >
                                    Skattemässigt lager (avdragsgill)
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => { setNedskrivningKrav(null); fetchInitial(); }}
                        disabled={refreshing || !allClassified}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Fortsätt med vald klassificering
                    </button>
                </>
            );
        }
        if (loadError) {
            return (
                <>
                    <div className="alert alert-danger" style={{ fontSize: '0.875rem' }}>
                        {loadError}
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchInitial()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Försök igen
                    </button>
                </>
            );
        }
        if (!data) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Hämtar skatteunderlaget…
                </p>
            );
        }
        if (booked) {
            return (
                <>
                    {completed && (
                        <div className="ye-well" style={{ marginTop: 0, marginBottom: '1.25rem' }}>
                            Steget är markerat som klart och året är låst. Ångra markeringen
                            längst ner om du behöver öppna året igen.
                        </div>
                    )}
                    {renderBookedState()}
                </>
            );
        }
        return (
            <>
                {renderUnderlag()}
                {renderFunds()}
                {renderUnderskott()}
                {renderComputeSection()}
            </>
        );
    };

    // ---- Layout -----------------------------------------------------------

    return (
        <>
            <style>{`
                .ye-tax-section + .ye-tax-section { margin-top: 2rem; }

                /* ?-tooltips */
                .ye-help {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    color: #9ca3af;
                    margin-left: 0.4rem;
                    cursor: help;
                    vertical-align: middle;
                }
                .ye-help:hover, .ye-help:focus { color: var(--primary); outline: none; }
                .ye-help-tip {
                    position: absolute;
                    bottom: calc(100% + 9px);
                    left: 50%;
                    transform: translateX(-50%);
                    width: 320px;
                    max-width: 78vw;
                    background: #1f2937;
                    color: #f9fafb;
                    font-size: 0.75rem;
                    font-weight: 400;
                    line-height: 1.55;
                    text-align: left;
                    padding: 0.65rem 0.8rem;
                    border-radius: 8px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity 0.12s ease;
                    pointer-events: none;
                    z-index: 40;
                    white-space: normal;
                }
                .ye-help-tip::after {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 6px solid transparent;
                    border-top-color: #1f2937;
                }
                .ye-help:hover .ye-help-tip, .ye-help:focus .ye-help-tip {
                    opacity: 1;
                    visibility: visible;
                }

                /* Fondrader — klickbara kort i stället för tabell */
                .ye-fund-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; max-width: 640px; }
                .ye-fund-row {
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    width: 100%;
                    text-align: left;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 0.7rem 0.9rem;
                    cursor: pointer;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .ye-fund-row:hover { border-color: var(--primary); }
                .ye-fund-row.selected {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-fund-row.forced { cursor: default; }
                .ye-fund-row:disabled { opacity: 1; }
                .ye-fund-check {
                    flex-shrink: 0;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    border: 2px solid #d1d5db;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    background: #fff;
                }
                .ye-fund-check.on { border-color: var(--primary); background: var(--primary); }
                .ye-fund-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
                .ye-fund-name { font-size: 0.875rem; color: var(--title, #374151); }
                .ye-fund-deadline { font-size: 0.75rem; color: #9ca3af; }
                .ye-fund-balance {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--title, #111827);
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .ye-fund-hint { font-size: 0.75rem; color: #9ca3af; }
                .ye-verifikat-link {
                    background: none;
                    border: none;
                    padding: 0;
                    font: inherit;
                    font-weight: 600;
                    color: var(--primary);
                    cursor: pointer;
                    text-decoration: underline;
                }
                .ye-verifikat-link:hover { color: var(--primary-hover, var(--primary)); }
                .ye-tax-rows { max-width: 560px; }
                .ye-tax-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 1rem;
                    padding: 0.35rem 0;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                }
                .ye-tax-row-group {
                    border-top: 1px solid #f3f4f6;
                    margin-top: 0.25rem;
                    padding-top: 0.5rem;
                    font-weight: 600;
                }
                .ye-tax-row-sub {
                    padding-left: 1rem;
                    color: #6b7280;
                    font-size: 0.8125rem;
                }
                .ye-tax-row .ye-num, .ye-tax-figure-value {
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .ye-tax-formula {
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin-top: 0.15rem;
                }
                .ye-tax-figures {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-top: 1rem;
                }
                .ye-tax-figure {
                    flex: 1 1 220px;
                    max-width: 340px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 0.875rem 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                .ye-tax-figure-label {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #6b7280;
                    display: inline-flex;
                    align-items: center;
                }
                .ye-tax-figure-value {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: var(--title, #111827);
                }
                .ye-inline-confirm {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    margin-top: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    font-size: 0.8125rem;
                    color: #4b5563;
                    max-width: 560px;
                }
                .ye-inline-confirm-actions {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }
            `}</style>

            <VerificationDetailsModal
                showVerificationDetails={showVerificationModal}
                setShowVerificationDetails={setShowVerificationModal}
                selectedRow={modalVerification}
                onVerificationUpdated={() => fetchInitial({ silent: true })}
                onUploadAttachment={() => {}}
                viewOnly
            />

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Skatt &amp; dispositioner</h4>
                    <p className="section-subtitle">
                        Fyra delar, uppifrån och ner: underlaget hämtas ur bokföringen (1),
                        du beslutar om periodiseringsfonder (2) och eventuellt underskott
                        från förra året (3), och till sist beräknar och bokför du hela
                        kedjan (4). Håll muspekaren över <HelpCircle size={13} style={{ verticalAlign: 'text-bottom' }} />-ikonerna
                        för förklaringar. När steget markeras som klart låses året.
                    </p>
                </div>
                <div className="action-buttons">
                    {booked ? (
                        <span className="ye-badge ye-badge-success">
                            <CheckCircle2 size={13} />
                            Skatten är bokförd
                        </span>
                    ) : (
                        <span className="ye-badge ye-badge-neutral">Inte bokförd ännu</span>
                    )}
                </div>
            </div>

            <div className="modern-card-body">
                {renderBody()}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Skatten är klar — året är låst
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            {busy ? 'Öppnar…' : 'Ångra markeringen — öppna året igen'}
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {booked
                                ? 'När du markerar steget som klart låses året — verifikat kan inte längre ändras.'
                                : 'Bokför skatten och årets resultat först — när steget markeras som klart låses året.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            <Lock size={16} />
                            {busy ? 'Sparar…' : 'Markera som klart och lås året'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default TaxStep;
